import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { ProjectAccessConstants } from "../../../../../../constants";
import { NavigationSearchParams, PrivateProjectAccessInviteAcceptParams } from "../../../../../../models";
import { acceptOrRejectProjectOrganisationInvitation } from "../../../../../../service/organisation";
import {
  getProjectOrganisationInvitationDetails,
  GetProjectOrganisationInvitationDetailsResponse,
} from "../../../../../../service/query";
import { Status } from "../../../../../../service/Shared";
import { getErrorMessageFromCode } from "../../../../../../service/ValidationErrorFormatter";
import { useAuth } from "../../../../../../useAuth";
import { useIsLoadingWrapper } from "../../../../../../utils";
import { getProjectAccessRoute, getSettingsProjectAccessRoute } from "../../../../../../utils/routes";
import { Toast } from "../../../../../../widget";

interface UseAcceptInvitationReturnData {
  invitationDetails?: GetProjectOrganisationInvitationDetailsResponse;
  handleBackClick: () => void;
  backButtonText: string;
  onAcceptInvitation: () => void;
  onRejectInvitation: () => void;
  hasPermission: (permission: string) => boolean;
  isOnAcceptInvitationLoading: boolean;
  isOnRejectInvitationLoading: boolean;
}

export const useAcceptInvitation = (): UseAcceptInvitationReturnData => {
  const navigate = useNavigate();
  const { hasPermission, currentUserType } = useAuth();

  const { projectOrganisationInvitationUuid } = useParams<PrivateProjectAccessInviteAcceptParams>();
  const [searchParams] = useSearchParams();
  // Opened from the project's Access tab (a request sent to this organisation), so Back returns there rather
  // than to the settings page - as the access details page does for the same flag.
  const isBackToProjectAccessTab = !!searchParams.get(NavigationSearchParams.projectAccessTab);

  const [invitationDetails, setInvitationDetails] = useState<GetProjectOrganisationInvitationDetailsResponse>();

  const [isOnAcceptInvitationLoading, setOnAcceptInvitationLoading] = useState(false);
  const [isOnRejectInvitationLoading, setOnRejectInvitationLoading] = useState(false);

  const getInvitationDetails = useCallback(
    async (invitationUuid: string) => {
      const response = await getProjectOrganisationInvitationDetails({
        projectOrganisationInvitationUuid: invitationUuid,
      });

      if (response.status === Status.Success && response.data) {
        setInvitationDetails(response.data);
      }
    },
    [projectOrganisationInvitationUuid]
  );

  useEffect(() => {
    if (projectOrganisationInvitationUuid) {
      getInvitationDetails(projectOrganisationInvitationUuid);
    }
  }, [projectOrganisationInvitationUuid]);

  const handleBackClick = (): void => {
    if (isBackToProjectAccessTab && invitationDetails?.project.uuid) {
      navigate(getProjectAccessRoute(invitationDetails.project.uuid, currentUserType));
      return;
    }

    navigate(getSettingsProjectAccessRoute(currentUserType));
  };

  const onAcceptInvitation = useIsLoadingWrapper(async (): Promise<void> => {
    if (projectOrganisationInvitationUuid && invitationDetails?.rowVersion) {
      const response = await acceptOrRejectProjectOrganisationInvitation({
        projectOrganisationInvitationUuid,
        rowVersion: invitationDetails.rowVersion,
        acceptOrReject: ProjectAccessConstants.INVITATION_RESPONSE.ACCEPT,
      });
      if (response.status === Status.Success) {
        Toast.success({
          message: "The project access invitation has been accepted",
        });
        setInvitationDetails({ ...invitationDetails, calculatedStatus: ProjectAccessConstants.STATUS_ACCEPTED });
      }
      if (response.status === Status.Error && response.errors && response.errors.length) {
        Toast.error({ message: getErrorMessageFromCode(response.errors[0].code) });
      }
    }
  }, setOnAcceptInvitationLoading);

  const onRejectInvitation = useIsLoadingWrapper(async (): Promise<void> => {
    if (projectOrganisationInvitationUuid && invitationDetails?.rowVersion) {
      const response = await acceptOrRejectProjectOrganisationInvitation({
        projectOrganisationInvitationUuid,
        rowVersion: invitationDetails.rowVersion,
        acceptOrReject: ProjectAccessConstants.INVITATION_RESPONSE.REJECT,
      });
      if (response.status === Status.Success) {
        Toast.success({ message: "The project access invitation has been rejected" });
        setInvitationDetails({ ...invitationDetails, calculatedStatus: ProjectAccessConstants.STATUS_REJECTED });
      }
      if (response.status === Status.Error && response.errors && response.errors.length) {
        Toast.error({ message: getErrorMessageFromCode(response.errors[0].code) });
      }
    }
  }, setOnRejectInvitationLoading);

  return {
    invitationDetails,
    handleBackClick,
    backButtonText: isBackToProjectAccessTab ? "Back to full project" : "Back to project access",
    onAcceptInvitation,
    onRejectInvitation,
    hasPermission,
    isOnAcceptInvitationLoading,
    isOnRejectInvitationLoading,
  };
};
